import { isAxiosError } from 'axios'

const INTERNAL_MESSAGE_PATTERNS = [
  /not a function/i,
  /\bis not a function\b/i,
  /TypeError|ReferenceError|SyntaxError|RangeError/i,
  /sequelize|Sequelize/i,
  /ECONNREFUSED|ECONNRESET|ETIMEDOUT|EPIPE/i,
  /\b(mysql|postgres|sqlite|ora-\d{5})\b/i,
  /\bat\s+\S+\s*\(/i,
  /node_modules/i,
  /\.[tj]s:\d+/i,
]

const MAX_USER_MESSAGE_LEN = 280

export function isSafeUserFacingMessage(message: string): boolean {
  const m = message.trim()
  if (!m || m.length > MAX_USER_MESSAGE_LEN) return false
  if (INTERNAL_MESSAGE_PATTERNS.some((p) => p.test(m))) return false
  return true
}

/** API `success: false` payloads — never trust blindly. */
export function sanitizeBusinessMessage(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string') return fallback
  const t = raw.trim()
  if (!t) return fallback
  return isSafeUserFacingMessage(t) ? t : fallback
}

export type UserFacingErrorOptions = {
  /** POST /auth/login — prefer a credential-style message on 401 */
  loginAttempt?: boolean
}

/**
 * Maps unknown failures (Axios, Error, etc.) to a short, merchant-safe string.
 * Server errors (5xx) and suspicious text never leak internals; dev console gets a hint.
 */
export function getUserFacingErrorMessage(
  error: unknown,
  fallback: string,
  options?: UserFacingErrorOptions,
): string {
  if (isAxiosError(error)) {
    const status = error.response?.status
    const data = error.response?.data

    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        return 'The request took too long. Please try again.'
      }
      return 'Could not reach the server. Check your connection and try again.'
    }

    if (status != null && status >= 500) {
      if (import.meta.env.DEV) {
        console.warn('[API]', status, data)
      }
      return 'Something went wrong on our end. Please try again in a moment.'
    }

    const apiMessage =
      data &&
      typeof data === 'object' &&
      data !== null &&
      'message' in data &&
      typeof (data as { message: unknown }).message === 'string'
        ? (data as { message: string }).message
        : ''

    if (status === 401) {
      if (options?.loginAttempt) {
        return sanitizeBusinessMessage(apiMessage, 'Invalid email or password.')
      }
      return sanitizeBusinessMessage(apiMessage, 'Please sign in again to continue.')
    }

    if (apiMessage && isSafeUserFacingMessage(apiMessage)) {
      return apiMessage
    }

    if (status === 404) return 'The requested resource was not found.'
    if (status === 403) return 'You do not have permission to do that.'
    if (status === 429) return 'Too many requests. Please wait a moment and try again.'
    if (status === 409) {
      return sanitizeBusinessMessage(
        apiMessage,
        'This conflicts with existing data. Refresh the page and try again.',
      )
    }

    return fallback
  }

  if (error instanceof Error) {
    if (isSafeUserFacingMessage(error.message)) return error.message
    return fallback
  }

  return fallback
}

export type UserFacingApiError = Error & { code?: string; status?: number }

/** Axios-aware Error with sanitized `.message`; keeps `.status` / `.code` for app logic. */
export function toUserFacingApiError(
  err: unknown,
  fallback: string,
  options?: UserFacingErrorOptions,
): UserFacingApiError {
  const message = getUserFacingErrorMessage(err, fallback, options)
  const next = new Error(message) as UserFacingApiError

  if (isAxiosError(err) && err.response) {
    next.status = err.response.status
    const raw = err.response.data
    if (raw && typeof raw === 'object' && raw !== null && 'error' in raw) {
      const code = (raw as { error?: { code?: string } }).error?.code
      if (code) next.code = code
    }
  }

  return next
}
